import { inject, observer } from 'mobx-react';
import { Lambda, observe } from 'mobx';
import * as React from 'react';

import { AnalyticsStore, QueryRoute, RouteStore } from 'bernie-core';
import { Context } from 'bernie-context';
import { Layer } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { UIPageStore } from 'stores/ui-page-store';

import { RateCalendarModel } from 'src/common/models/common/rate-calendar-model';

import { RateCalendarFooter } from 'src/common/components/search/rate-calendar-footer';
import { DayName, UitkDatePickerProps, UitkNewDatePicker } from 'uitk-react-date-picker';
import { PropertyRateCalendar } from '__generated__/api';

export interface NewDateFieldProps {
  analytics?: AnalyticsStore;
  context?: Context;
  dateFieldProps: object;
  fetchDatePickerRates?: () => Promise<RateCalendarModel>;
  isDateless?: boolean;
  midnightBookingOffsetHours?: number;
  numDaysBookableInAdvance?: number;
  updateModels: (startDate: string, EndDate: string) => void;
  registerFocusMethod?: (fieldName: string, focusMethod: () => void) => void;
  rfrr: object;
  routeStore?: RouteStore;
  currentHotel?: CurrentHotelStore;
  uiPage?: UIPageStore;
}

export type LocalizedNewDateFieldProps = LocalizedComponentProps & UitkDatePickerProps & NewDateFieldProps;

export interface NewDateFieldState {
  mounted: boolean;
  datePickerContent?: RateCalendarModel['datePickerContent'];
  footerLegends?: PropertyRateCalendar.Legend[];
  footerText?: string;
  showDatePicker: boolean;
}

@withLocalization
@inject('routeStore', 'analytics', 'context', 'currentHotel', 'uiPage')
@observer
export class NewDateField extends React.Component<LocalizedNewDateFieldProps, NewDateFieldState> {
  public state: Readonly<NewDateFieldState> = {
    mounted: false,
    datePickerContent: {},
    footerLegends: [],
    footerText: undefined,
    showDatePicker: false,
  };

  constructor(props) {
    super(props);

    this.focusButton = React.createRef();

    this.focusDateFieldInput = this.focusDateFieldInput.bind(this);
    this.onMount = this.onMount.bind(this);
    this.dismiss = this.dismiss.bind(this);
    this.open = this.open.bind(this);
    this.save = this.save.bind(this);
  }

  public focusButton = null;
  public startRef;
  public endRef;
  private datePickerAnalytics = {};
  private disposerOpenDatePicker: Lambda;
  private disposerUiPageOpenDatePickerEvent: Lambda;

  public static defaultProps = {
    endDate: '',
    startDate: '',
    l10n: null,
  };

  public componentDidMount() {
    const { registerFocusMethod, currentHotel, uiPage } = this.props;
    registerFocusMethod('endDate', this.focusDateFieldInput);
    this.refreshRates();
    this.setState({ mounted: true });
    this.disposerOpenDatePicker = observe(currentHotel, (change) => {
      if (change.type !== 'remove') {
        const { newValue, name } = change;
        if (name === 'openNewDatePicker' && newValue) {
          this.startRef?.current?.click();
          currentHotel.setOpenNewDatePicker(false);
        }
      }
    });
    this.disposerUiPageOpenDatePickerEvent = observe(uiPage, (change) => {
      if (change.type !== 'remove') {
        const { newValue, name } = change;
        if (name === 'openNewDatePickerEvent' && newValue) {
          this.setState({ showDatePicker: true });
        }
      }
    });
  }

  public componentWillUnmount() {
    if (this.disposerOpenDatePicker) {
      this.disposerOpenDatePicker();
    }
    if (this.disposerUiPageOpenDatePickerEvent) {
      this.disposerUiPageOpenDatePickerEvent();
    }
  }

  public open(dateType, isMobile) {
    const { routeStore } = this.props;

    if (isMobile) {
      routeStore.updateSearch({ pwaDialog: `datepicker-${dateType}` });
    }

    this.refreshRates();
    this.setState({ showDatePicker: true });
  }

  private refreshRates(): void {
    const { fetchDatePickerRates } = this.props;
    this.resetDatePickerRates();

    if (fetchDatePickerRates && typeof fetchDatePickerRates === 'function') {
      fetchDatePickerRates().then((rateCalendar: RateCalendarModel) => {
        if (rateCalendar && typeof rateCalendar === 'object') {
          this.setState({
            datePickerContent: rateCalendar.datePickerContent,
            footerLegends: rateCalendar.footerHtml,
            footerText: rateCalendar.disclaimer,
          });
        }
      });
    }
  }

  private resetDatePickerRates() {
    this.setState({
      datePickerContent: null,
      footerLegends: [],
      footerText: null,
    });
  }

  public save(start, end, isMobile) {
    const { endDate, l10n, startDate, updateModels } = this.props;
    const { formatDate } = l10n;
    const formattedEnd = formatDate(end, { raw: 'yyyy-MM-dd' });
    const formattedStart = formatDate(start, { raw: 'yyyy-MM-dd' });

    // prevents submitting the datepicker when no new dates are selected
    if (endDate !== formattedEnd || startDate !== formattedStart) {
      updateModels(formattedStart, formattedEnd);
    }

    this.dismiss(isMobile, false);
  }

  public focusDateFieldInput(): void {
    const { dateFieldProps } = this.props;
    const { endInvalid, startInvalid } = dateFieldProps;

    if (startInvalid) {
      this.focusButton = this.startRef && this.startRef.current;
    }

    if (endInvalid) {
      this.focusButton = this.endRef && this.endRef.current;
    }

    // manage focus back to the trigger
    if (this.focusButton && typeof this.focusButton.focus === 'function') {
      this.focusButton.focus();
    }
  }

  public dismiss(isMobile, noUpdate) {
    if (isMobile) {
      this.props.routeStore.goBack();
    }

    if (!noUpdate) {
      this.setState({ showDatePicker: false });
    }
  }

  public onMount(start, end) {
    this.startRef = start;
    this.endRef = end;
  }

  public render() {
    const {
      analytics,
      buttonText,
      context,
      dateFieldProps,
      endDate,
      isDateless,
      midnightBookingOffsetHours,
      numDaysBookableInAdvance,
      startDate,
      l10n,
    } = this.props;

    const { mounted, showDatePicker } = this.state;
    const firstDayOfWeek = parseInt(DayName[l10n.getWeekData().firstDay.toUpperCase()], 10);

    this.datePickerAnalytics = {
      closeAnalytics: analytics.setupTracker(':ChangeDatesClose', 'Close Date Picker', null),
      doneAnalytics: isDateless
        ? analytics.setupTracker(':DatepickerSearch', 'Search Date Picker', null)
        : analytics.setupTracker(':ChangeDatesDone', 'Apply Date Picker', null),
      nextPageAnalytics: analytics.setupTracker(':NextMonthClick', 'Next Month Click', null),
      previousPageAnalytics: analytics.setupTracker(':PreviousMonthClick', 'Previous Month Click', null),
    };

    let enhancedDateFieldProps = {
      ...dateFieldProps,
      showDatePicker,
      endAnalytics: analytics.setupTracker(':ChangeDatesCheckOut', 'Open Date Picker'),
      startAnalytics: analytics.setupTracker(':ChangeDatesCheckIn', 'Open Date Picker'),
    };

    if (!mounted) {
      enhancedDateFieldProps = {
        ...enhancedDateFieldProps,
        endPlaceholder: context.localDateFormat,
        startPlaceholder: context.localDateFormat,
      };
    }

    const dialogWrapper = (children, dateType) => (
      <QueryRoute query={{ pwaDialog: `datepicker-${dateType}` }}>
        <Layer id={`home-datepicker-${dateType}`} onDismiss={this.dismiss}>
          {children}
        </Layer>
      </QueryRoute>
    );

    const datePickerProps = {
      analytics: this.datePickerAnalytics,
      buttonText,
      dateFieldProps: enhancedDateFieldProps,
      datePickerContent: this.state.datePickerContent,
      daysBookableInAdvance: numDaysBookableInAdvance,
      dialogWrapper,
      doubleMonths: true,
      endDate,
      footerText: this.state.footerText,
      footerHtml: <RateCalendarFooter legends={this.state.footerLegends} />,
      midnightBookingOffset: midnightBookingOffsetHours,
      onDismissCB: this.dismiss,
      onMountCB: this.onMount,
      onOpenCB: this.open,
      onSubmitCB: this.save,
      startDate,
      firstDayOfWeek,
    };

    return <UitkNewDatePicker {...datePickerProps} />;
  }
}
